import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Trash, ShieldCheck, ArrowsClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { fmtInr, getStatusBadgeClass } from './helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useDuplicateScan = () => {
  const [scan, setScan] = useState({ groups: [], total_groups: 0, total_extra: 0 });
  const [loading, setLoading] = useState(false);
  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/candidates/duplicates`);
      setScan(data);
    } catch { /* owner-only; ignore for other roles */ } finally { setLoading(false); }
  }, []);
  return { scan, loading, run };
};

const Row = ({ c, isNewest, onDelete }) => (
  <tr className="border-t border-[#143E29] text-white" data-testid={`dup-row-${c.id}`}>
    <td className="px-3 py-2">
      <div className="font-black">{c.candidate_name}</div>
      <div className="text-[10px] text-white/70 font-medium">{c.candidate_number} · {c.recruiter_name || '—'}{isNewest && <span className="ml-1 px-1 rounded bg-[#FDE047] text-black font-bold">NEWEST</span>}</div>
    </td>
    <td className="px-3 py-2 text-xs font-bold whitespace-nowrap">{c.interview_date}<br /><span className="text-white/70">joins {c.date_of_joining}</span></td>
    <td className="px-3 py-2"><span className={getStatusBadgeClass(c.joining_status)}>{c.joining_status}</span></td>
    <td className="px-3 py-2 text-xs font-bold whitespace-nowrap">
      {fmtInr(c.effective_charge)}
      <div className="text-[10px] text-[#A3E635]">{c.payments_count > 0 ? `${c.payments_count} payment${c.payments_count > 1 ? 's' : ''} · paid ${fmtInr(c.total_paid)}` : 'no payments'}{c.agreement_sent ? ' · agreement sent' : ''}</div>
    </td>
    <td className="px-3 py-2 text-right">
      <button
        data-testid={`dup-delete-${c.id}`}
        onClick={() => onDelete(c)}
        title={c.payments_count > 0 ? 'Has payments — delete with care' : 'Delete this duplicate'}
        className={`p-2 border-2 border-black rounded hover:-translate-y-0.5 transition-all ${c.payments_count > 0 ? 'bg-[#F59E0B] text-black' : 'bg-[#F87171] text-white'}`}
      >
        <Trash size={16} weight="bold" />
      </button>
    </td>
  </tr>
);

export const DuplicatesDialog = ({ open, onOpenChange, scan, loading, onRescan, onDeleted }) => {
  useEffect(() => { if (open) onRescan(); }, [open, onRescan]);

  const del = async (c) => {
    const extra = c.payments_count > 0 ? `\n\nWARNING: this record has ${c.payments_count} payment(s) totalling ₹${c.total_paid}. Payments will be lost.` : '';
    if (!window.confirm(`Delete duplicate "${c.candidate_name}" (${c.candidate_number}) at ${c.company_name}?${extra}`)) return;
    try {
      await axios.delete(`${API}/candidates/${c.id}`);
      toast.success(`Deleted duplicate ${c.candidate_name}`);
      onRescan();
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F3622] border-2 border-[#F59E0B] shadow-[6px_6px_0px_0px_rgba(245,158,11,0.4)] sm:max-w-[900px] max-h-[88vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <Copy size={28} weight="duotone" className="text-[#F59E0B]" /> Duplicate Cleanup Scan
          </DialogTitle>
          <DialogDescription className="text-xs text-white/80">
            Candidates sharing the same mobile number <b>and</b> company. Keep one per group and delete the rest — records with payments are marked orange.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <span data-testid="dup-summary" className={`px-3 py-1 border-2 border-black rounded-md text-xs font-black ${scan.total_groups ? 'bg-[#F59E0B] text-black' : 'bg-[#4ADE80] text-black'}`}>
            {scan.total_groups ? `${scan.total_groups} group${scan.total_groups > 1 ? 's' : ''} · ${scan.total_extra} extra record${scan.total_extra > 1 ? 's' : ''}` : 'No duplicates found'}
          </span>
          <Button data-testid="dup-rescan" onClick={onRescan} disabled={loading} className="ml-auto bg-white text-black border-2 border-black rounded-md text-xs font-bold px-3 py-1.5 hover:-translate-y-0.5 transition-all">
            <ArrowsClockwise size={14} weight="bold" className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Rescan
          </Button>
        </div>
        {scan.groups.length === 0 && !loading && (
          <div className="flex flex-col items-center gap-2 py-10 text-white" data-testid="dup-empty">
            <ShieldCheck size={48} weight="duotone" className="text-[#4ADE80]" />
            <div className="font-black">Tracker is clean — every mobile + company pair is unique.</div>
          </div>
        )}
        <div className="space-y-4">
          {scan.groups.map((g) => (
            <div key={`${g.mobile}-${g.company_name}`} className="border-2 border-[#F59E0B] rounded-md overflow-hidden" data-testid={`dup-group-${g.mobile}`}>
              <div className="bg-[#F59E0B] text-black px-3 py-1.5 text-xs font-black uppercase tracking-wider flex items-center gap-2">
                {g.company_name} · {g.mobile} <span className="ml-auto px-2 rounded bg-black text-[#FDE047]">{g.count} records</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#143E29] text-[#FDE047] text-[10px] uppercase tracking-wider text-left">
                    <th className="px-3 py-1.5">Candidate</th><th className="px-3 py-1.5">Interview / Joining</th><th className="px-3 py-1.5">Status</th><th className="px-3 py-1.5">Charge / Payments</th><th className="px-3 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {g.candidates.map((c, i) => <Row key={c.id} c={c} isNewest={i === g.candidates.length - 1} onDelete={del} />)}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
